// 271 Response Parser
//
// Turns Stedi's eligibility response into the normalized shape the UI renders.
// Deliberately dependency-free (no Firebase, no browser APIs) so the Phase 2
// Vercel endpoint can run the exact same logic server-side — the parsing rules
// must not drift between mock and production.
//
// Guiding rule: absence of evidence is not evidence of absence. Every field a
// payer stayed silent about stays `undefined` (or null for the tri-state flags)
// so the UI can say "Unknown" instead of inventing a "No".

import {
  BenefitsInformation,
  BenefitsRelatedEntity,
  CARVE_OUT_ENTITY_IDENTIFIERS,
  CarveOutInfo,
  CoverageStatus,
  EligibilityResponse,
  EligibilityResponseError,
  NormalizedBenefits,
  TELEHEALTH_KEYWORDS,
  TELEHEALTH_PLACE_OF_SERVICE_CODES,
} from '../types/eligibility';

export interface ParsedEligibility {
  status: CoverageStatus;
  statusReason?: string;
  benefits: NormalizedBenefits;
  carveOut: CarveOutInfo;
  errors?: EligibilityResponseError[];
}

// ─── Value coercion ───────────────────────────────────────────────────

/** Parse a 271 money string. Returns undefined for missing/garbage values. */
function toAmount(value?: string): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = parseFloat(String(value).replace(/[$,]/g, ''));
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Parse a 271 percentage. Payers send both conventions for the same idea:
 * "20" and "0.2" both mean 20%. Values at or below 1 are treated as fractions.
 */
function toPercent(value?: string): number | undefined {
  const parsed = toAmount(value);
  if (parsed === undefined) return undefined;
  return parsed <= 1 ? Math.round(parsed * 1000) / 10 : parsed;
}

function toQuantity(value?: string): number | undefined {
  const parsed = toAmount(value);
  return parsed === undefined ? undefined : Math.round(parsed);
}

/** Time qualifier '29' = Remaining. Everything else is a period total. */
function isRemaining(entry: BenefitsInformation): boolean {
  return entry.timeQualifierCode === '29';
}

function isInNetwork(entry: BenefitsInformation): boolean | null {
  if (entry.inPlanNetworkIndicatorCode === 'Y') return true;
  if (entry.inPlanNetworkIndicatorCode === 'N') return false;
  return null;
}

/** All free text attached to one benefit entry, for keyword scanning. */
function entryText(entry: BenefitsInformation): string {
  const parts: string[] = [];
  if (entry.name) parts.push(entry.name);
  if (entry.planCoverage) parts.push(entry.planCoverage);
  if (entry.insuranceType) parts.push(entry.insuranceType);
  (entry.additionalInformation || []).forEach((info) => {
    if (info.description) parts.push(info.description);
  });
  return parts.join(' | ');
}

// ─── Entry selection ──────────────────────────────────────────────────

/**
 * Rank benefit entries so the most specific wins when a payer returns several
 * for the same concept: requested service type beats generic, in-network beats
 * out-of-network, and an entry that names an amount beats one that doesn't.
 */
function relevanceScore(entry: BenefitsInformation, serviceTypeCode?: string): number {
  let score = 0;
  if (serviceTypeCode && (entry.serviceTypeCodes || []).includes(serviceTypeCode)) score += 4;
  if (entry.inPlanNetworkIndicatorCode === 'Y') score += 2;
  if (entry.benefitAmount || entry.benefitPercent || entry.benefitQuantity) score += 1;
  return score;
}

function entriesWithCode(
  response: EligibilityResponse,
  code: string,
  serviceTypeCode?: string
): BenefitsInformation[] {
  return (response.benefitsInformation || [])
    .filter((entry) => entry.code === code)
    .sort((a, b) => relevanceScore(b, serviceTypeCode) - relevanceScore(a, serviceTypeCode));
}

function bestEntry(
  response: EligibilityResponse,
  code: string,
  serviceTypeCode?: string
): BenefitsInformation | undefined {
  return entriesWithCode(response, code, serviceTypeCode)[0];
}

// ─── Coverage status ──────────────────────────────────────────────────

function parseStatus(
  response: EligibilityResponse,
  serviceTypeCode?: string
): { status: CoverageStatus; statusReason?: string } {
  if (response.errors && response.errors.length > 0) {
    const first = response.errors[0];
    return {
      status: 'error',
      statusReason: first.description || first.code || 'Payer returned an error',
    };
  }

  const active = bestEntry(response, '1', serviceTypeCode);
  if (active) {
    return { status: 'active', statusReason: active.planCoverage || active.name };
  }

  const inactive =
    bestEntry(response, '6', serviceTypeCode) || bestEntry(response, '7', serviceTypeCode);
  if (inactive) {
    return {
      status: 'inactive',
      statusReason: inactive.planCoverage || inactive.name || 'Coverage not active',
    };
  }

  // Some payers answer only through planStatus rather than benefitsInformation.
  const planStatus = (response.planStatus || [])[0];
  if (planStatus?.statusCode === '1') {
    return { status: 'active', statusReason: planStatus.status || planStatus.planDetails };
  }
  if (planStatus?.statusCode === '6' || planStatus?.statusCode === '7') {
    return { status: 'inactive', statusReason: planStatus.status || planStatus.planDetails };
  }

  return {
    status: 'unknown',
    statusReason: 'Payer response contained no coverage status',
  };
}

// ─── Telehealth ───────────────────────────────────────────────────────

/**
 * Telehealth is never a dedicated field — payers signal it with a place-of-service
 * code or bury it in free text. Returns null when neither appears, which is the
 * common case and must not be shown as "not covered".
 */
function parseTelehealth(response: EligibilityResponse): {
  covered: boolean | null;
  evidence?: string;
} {
  const entries = response.benefitsInformation || [];

  for (const entry of entries) {
    for (const info of entry.eligibilityAdditionalInformationList || []) {
      if (info.industryCode && TELEHEALTH_PLACE_OF_SERVICE_CODES.includes(info.industryCode)) {
        // A POS code on a non-covered entry means the opposite of coverage.
        const covered = entry.code !== 'I' && entry.code !== '6';
        return { covered, evidence: `Place of service ${info.industryCode}` };
      }
    }
  }

  for (const entry of entries) {
    const text = entryText(entry).toUpperCase();
    const keyword = TELEHEALTH_KEYWORDS.find((word) => text.includes(word));
    if (keyword) {
      const covered = entry.code !== 'I' && entry.code !== '6';
      return { covered, evidence: entryText(entry) };
    }
  }

  return { covered: null };
}

// ─── Benefits ─────────────────────────────────────────────────────────

function parseBenefits(
  response: EligibilityResponse,
  serviceTypeCode?: string
): NormalizedBenefits {
  const benefits: NormalizedBenefits = {};

  const copay = bestEntry(response, 'B', serviceTypeCode);
  if (copay) benefits.copay = toAmount(copay.benefitAmount);

  const coinsurance = bestEntry(response, 'A', serviceTypeCode);
  if (coinsurance) benefits.coinsurancePercent = toPercent(coinsurance.benefitPercent);

  for (const entry of entriesWithCode(response, 'C', serviceTypeCode)) {
    const amount = toAmount(entry.benefitAmount);
    if (amount === undefined) continue;
    if (isRemaining(entry)) {
      if (benefits.deductibleRemaining === undefined) benefits.deductibleRemaining = amount;
    } else if (benefits.deductibleTotal === undefined) {
      benefits.deductibleTotal = amount;
    }
  }

  for (const entry of entriesWithCode(response, 'G', serviceTypeCode)) {
    const amount = toAmount(entry.benefitAmount);
    if (amount === undefined) continue;
    if (isRemaining(entry)) {
      if (benefits.outOfPocketRemaining === undefined) benefits.outOfPocketRemaining = amount;
    } else if (benefits.outOfPocketTotal === undefined) {
      benefits.outOfPocketTotal = amount;
    }
  }

  // Visit limits arrive as limitations ('F') counted in visits ('VS').
  for (const entry of entriesWithCode(response, 'F', serviceTypeCode)) {
    if (entry.quantityQualifierCode !== 'VS') continue;
    const quantity = toQuantity(entry.benefitQuantity);
    if (quantity === undefined) continue;
    if (isRemaining(entry)) {
      if (benefits.visitLimitRemaining === undefined) benefits.visitLimitRemaining = quantity;
    } else if (benefits.visitLimitTotal === undefined) {
      benefits.visitLimitTotal = quantity;
    }
  }

  const authFlags = (response.benefitsInformation || [])
    .filter((entry) => entry.authOrCertIndicator === 'Y' || entry.authOrCertIndicator === 'N')
    .sort((a, b) => relevanceScore(b, serviceTypeCode) - relevanceScore(a, serviceTypeCode));
  benefits.priorAuthRequired = authFlags.length
    ? authFlags[0].authOrCertIndicator === 'Y'
    : null;

  const telehealth = parseTelehealth(response);
  benefits.telehealthCovered = telehealth.covered;
  if (telehealth.evidence) benefits.telehealthEvidence = telehealth.evidence;

  const networkEntry = (response.benefitsInformation || []).find(
    (entry) => isInNetwork(entry) !== null
  );
  benefits.inNetwork = networkEntry ? isInNetwork(networkEntry) : null;

  const plan = response.planInformation || {};
  const planDates = response.planDateInformation || {};
  benefits.planName =
    plan.planDescription ||
    plan.groupDescription ||
    (response.planStatus || [])[0]?.planDetails ||
    undefined;
  benefits.groupNumber = plan.groupNumber || undefined;
  benefits.planBeginDate = planDates.planBegin || planDates.eligibilityBegin || undefined;
  benefits.planEndDate = planDates.planEnd || planDates.eligibilityEnd || undefined;

  return benefits;
}

// ─── MBHO carve-out detection ─────────────────────────────────────────

function relatedEntities(entry: BenefitsInformation): BenefitsRelatedEntity[] {
  const entities = entry.benefitsRelatedEntities || [];
  return entry.benefitsRelatedEntity ? [entry.benefitsRelatedEntity, ...entities] : entities;
}

function phoneOf(entity: BenefitsRelatedEntity): string | undefined {
  const contacts = entity.contactInformation?.contacts || [];
  return contacts.find((contact) => contact.communicationMode === 'Telephone')?.communicationNumber;
}

/**
 * Detect a behavioral-health carve-out: the primary payer telling us someone
 * else administers these benefits. This is the single highest-value signal in
 * the whole response — missing it is how behavioral practices end up with
 * 22-30% denial rates on claims they believed were covered.
 */
export function detectCarveOut(
  response: EligibilityResponse,
  primaryPayerName?: string
): CarveOutInfo {
  const primary = (primaryPayerName || response.payer?.name || '').trim().toUpperCase();

  for (const entry of response.benefitsInformation || []) {
    for (const entity of relatedEntities(entry)) {
      if (!entity.entityIdentifier) continue;
      if (!CARVE_OUT_ENTITY_IDENTIFIERS.includes(entity.entityIdentifier)) continue;

      const name = (entity.entityName || '').trim();
      // The primary payer often lists itself; that is not a carve-out.
      if (!name || name.toUpperCase() === primary) continue;

      // Qualifier 'MI' means the value is the member's ID with the carve-out
      // administrator — that is what the second-pass call must be keyed on.
      // Any other qualifier ('PI', 'XV', ...) identifies the payer instead.
      const isMemberId = entity.entityIdentification === 'MI';

      return {
        detected: true,
        entityName: name,
        payerIdentifier: isMemberId ? undefined : entity.entityIdentificationValue,
        memberId: isMemberId ? entity.entityIdentificationValue : undefined,
        phone: phoneOf(entity),
        secondPassPerformed: false,
      };
    }
  }

  return { detected: false, secondPassPerformed: false };
}

// ─── Entry point ──────────────────────────────────────────────────────

/**
 * Parse a 271 into the normalized shape.
 * `serviceTypeCode` is the STC that was requested — used to prefer benefit
 * entries that actually describe the service we asked about.
 */
export function parseEligibilityResponse(
  response: EligibilityResponse,
  serviceTypeCode?: string
): ParsedEligibility {
  const { status, statusReason } = parseStatus(response, serviceTypeCode);

  return {
    status,
    statusReason,
    benefits: status === 'error' ? {} : parseBenefits(response, serviceTypeCode),
    carveOut: detectCarveOut(response),
    errors: response.errors,
  };
}

/**
 * Merge the MBHO second-pass result over the primary payer result.
 * The carve-out administrator is authoritative for behavioral benefits, so its
 * values win — but only where it actually supplied one.
 */
export function mergeCarveOutResult(
  primary: ParsedEligibility,
  secondPass: ParsedEligibility,
  carveOut: CarveOutInfo
): ParsedEligibility {
  const merged: NormalizedBenefits = { ...primary.benefits };

  (Object.keys(secondPass.benefits) as Array<keyof NormalizedBenefits>).forEach((key) => {
    const value = secondPass.benefits[key];
    if (value !== undefined && value !== null) {
      (merged as Record<string, unknown>)[key] = value;
    }
  });

  return {
    // The MBHO decides whether behavioral care is covered, even if the primary
    // plan is active overall.
    status: secondPass.status === 'unknown' ? primary.status : secondPass.status,
    statusReason: secondPass.statusReason || primary.statusReason,
    benefits: merged,
    carveOut: {
      ...carveOut,
      secondPassPerformed: true,
      secondPassStatus: secondPass.status,
    },
    errors: [...(primary.errors || []), ...(secondPass.errors || [])].length
      ? [...(primary.errors || []), ...(secondPass.errors || [])]
      : undefined,
  };
}
